import { c as _c } from "react/compiler-runtime";
import React from "react";
import { PortableText, type PortableTextComponents } from "next-sanity";
import type { PortableTextBlock } from "@portabletext/types";
import { SECTION_CONTAINER_WIDTHS, type SectionContainerWidth } from "@/components/atoms/section-constants";
import { SectionBackground } from "../modules/Section/SectionBackground";
import { SectionContent } from "../modules/Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import type { SanityImageSource } from "@/components/atoms/SanityImage";
import { SanityImage } from "@/components/atoms/SanityImage";
import { PortableStatistics } from "@/components/modules/PortableStatistics";
import PortablePullQuote from "./PortablePullQuote";
import { Box } from "@/components/atoms/Box";

// Types for column content
type ColumnContent = PortableTextBlock | {
  _type: "sanityImage";
  _key: string;
  public_id?: string;
  context?: {
    custom?: {
      alt?: string;
    };
  };
} | {
  _type: "portableStatistics";
  _key: string;
  heading?: string;
  statistics: Array<{
    _key?: string;
    value: string;
    icon?: string;
    label: string;
    source?: string;
    sourceUrl?: string;
  }>;
  layout?: "row" | "grid" | "stacked" | "featured";
  style?: Record<string, unknown>;
} | {
  _type: "portablePullQuote";
  _key: string;
  quote: string;
  author: string;
  company?: string;
  style?: Record<string, unknown>;
};
interface PortableTwoColumnProps {
  value: {
    leftColumn: ColumnContent[];
    rightColumn: ColumnContent[];
    columnSplit?: "50-50" | "60-40" | "40-60" | "70-30" | "30-70";
    verticalAlign?: "start" | "center" | "end";
    reverseOnMobile?: boolean;
    style?: {
      backgroundColor?: string;
      backgroundImage?: SanityImageSource;
      backgroundVideo?: Record<string, unknown>;
      backgroundPattern?: string;
      patternOpacity?: number;
      textColor?: string;
      paddingTop?: string;
      paddingBottom?: string;
      containerWidth?: string;
      customClasses?: string;
    };
  };
}
const paddingMapping: Record<string, string> = {
  "pt-0": "pt-0",
  "pt-8": "pt-8",
  "pt-16": "pt-16",
  "pt-24": "pt-24",
  "pt-32": "pt-32",
  "pb-0": "pb-0",
  "pb-8": "pb-8",
  "pb-16": "pb-16",
  "pb-24": "pb-24",
  "pb-32": "pb-32"
};

// Column width mappings based on split
const columnSplitClasses: Record<string, {
  left: string;
  right: string;
}> = {
  "50-50": {
    left: "md:col-span-6",
    right: "md:col-span-6"
  },
  "60-40": {
    left: "md:col-span-7",
    right: "md:col-span-5"
  },
  "40-60": {
    left: "md:col-span-5",
    right: "md:col-span-7"
  },
  "70-30": {
    left: "md:col-span-8",
    right: "md:col-span-4"
  },
  "30-70": {
    left: "md:col-span-4",
    right: "md:col-span-8"
  }
};

// Vertical alignment mapping
const verticalAlignClasses: Record<string, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end"
};

// Components for rendering nested portable text content within columns
const createColumnComponents = (): PortableTextComponents => ({
  types: {
    sanityImage: ({
      value
    }) => <Box className="my-6 first:mt-0 last:mb-0">
        <SanityImage image={value} className="w-full rounded-lg" sizes="(max-width: 768px) 100vw, 50vw" />
      </Box>,
    portableStatistics: ({
      value
    }) => <Box className="my-6 first:mt-0 last:mb-0">
        <PortableStatistics heading={value.heading} statistics={value.statistics} layout={value.layout} style={value.style} />
      </Box>,
    portablePullQuote: ({
      value
    }) => <Box className="my-6 first:mt-0 last:mb-0">
        <PortablePullQuote value={value} />
      </Box>
  }
});

/**
 * PortableTwoColumn Component
 *
 * A flexible side-by-side content layout for visual variety and efficient space usage.
 * Supports configurable column splits, vertical alignment, and mobile stacking behavior.
 */
const PortableTwoColumn = t0 => {
  const $ = _c(75);
  const {
    value
  } = t0;
  const {
    leftColumn,
    rightColumn,
    columnSplit: t1,
    verticalAlign: t2,
    reverseOnMobile: t3,
    style
  } = value;
  const columnSplit = t1 === undefined ? "50-50" : t1;
  const verticalAlign = t2 === undefined ? "start" : t2;
  const reverseOnMobile = t3 === undefined ? false : t3;
  const bgColor = style?.backgroundColor || "transparent";
  const paddingTop = style?.paddingTop || "pt-8";
  const paddingBottom = style?.paddingBottom || "pb-8";
  const containerWidthValue = style?.containerWidth || "max-w-7xl";
  const ptClass = paddingMapping[paddingTop] || "pt-8";
  const pbClass = paddingMapping[paddingBottom] || "pb-8";
  const containerSize = (containerWidthValue in SECTION_CONTAINER_WIDTHS ? containerWidthValue : "max-w-7xl") as SectionContainerWidth;
  let T0;
  let T1;
  let T2;
  let T3;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== bgColor || $[1] !== columnSplit || $[2] !== containerSize || $[3] !== leftColumn || $[4] !== pbClass || $[5] !== ptClass || $[6] !== reverseOnMobile || $[7] !== rightColumn || $[8] !== style?.backgroundImage || $[9] !== style?.backgroundPattern || $[10] !== style?.backgroundVideo || $[11] !== style?.customClasses || $[12] !== style?.patternOpacity || $[13] !== verticalAlign) {
    t18 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const isDark = isDarkColor(bgColor);
      const splitClasses = columnSplitClasses[columnSplit] || columnSplitClasses["50-50"];
      const alignClass = verticalAlignClasses[verticalAlign] || verticalAlignClasses.start;
      let t19;
      if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = createColumnComponents();
        $[33] = t19;
      } else {
        t19 = $[33];
      }
      const columnComponents = t19;
      if ((!leftColumn || leftColumn.length === 0) && (!rightColumn || rightColumn.length === 0)) {
        t18 = null;
        break bb0;
      }
      T3 = SectionBackground;
      t9 = "section";
      t10 = "Content";
      const t20 = bgColor !== "transparent" && "rounded-2xl md:rounded-3xl border border-white/10 shadow-lg";
      const t21 = style?.customClasses;
      if ($[34] !== pbClass || $[35] !== ptClass || $[36] !== t20 || $[37] !== t21) {
        t11 = cn("not-prose two-column-layout my-12", t20, ptClass, pbClass, t21);
        $[34] = pbClass;
        $[35] = ptClass;
        $[36] = t20;
        $[37] = t21;
        $[38] = t11;
      } else {
        t11 = $[38];
      }
      t12 = bgColor;
      t13 = style?.backgroundImage;
      t14 = style?.backgroundVideo;
      t15 = style?.backgroundPattern as BackgroundPatternType;
      t16 = style?.patternOpacity;
      t17 = isDark;
      T2 = SectionContent;
      t8 = containerSize;
      T1 = Box;
      if ($[39] !== alignClass) {
        t6 = cn("relative z-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12", alignClass);
        $[39] = alignClass;
        $[40] = t6;
      } else {
        t6 = $[40];
      }
      const t22 = reverseOnMobile && "order-2 md:order-1";
      let t23;
      if ($[41] !== splitClasses.left || $[42] !== t22) {
        t23 = cn("col-span-1", splitClasses.left, t22);
        $[41] = splitClasses.left;
        $[42] = t22;
        $[43] = t23;
      } else {
        t23 = $[43];
      }
      const t24 = leftColumn && leftColumn.length > 0 && <Box className={cn("prose max-w-none", isDark && "prose-invert")}><PortableText value={leftColumn} components={columnComponents} /></Box>;
      if ($[44] !== t23 || $[45] !== t24) {
        t7 = <Box className={t23}>{t24}</Box>;
        $[44] = t23;
        $[45] = t24;
        $[46] = t7;
      } else {
        t7 = $[46];
      }
      T0 = Box;
      const t25 = reverseOnMobile && "order-1 md:order-2";
      if ($[47] !== splitClasses.right || $[48] !== t25) {
        t4 = cn("col-span-1", splitClasses.right, t25);
        $[47] = splitClasses.right;
        $[48] = t25;
        $[49] = t4;
      } else {
        t4 = $[49];
      }
      t5 = rightColumn && rightColumn.length > 0 && <Box className={cn("prose max-w-none", isDark && "prose-invert")}><PortableText value={rightColumn} components={columnComponents} /></Box>;
    }
    $[0] = bgColor;
    $[1] = columnSplit;
    $[2] = containerSize;
    $[3] = leftColumn;
    $[4] = pbClass;
    $[5] = ptClass;
    $[6] = reverseOnMobile;
    $[7] = rightColumn;
    $[8] = style?.backgroundImage;
    $[9] = style?.backgroundPattern;
    $[10] = style?.backgroundVideo;
    $[11] = style?.customClasses;
    $[12] = style?.patternOpacity;
    $[13] = verticalAlign;
    $[14] = T0;
    $[15] = T1;
    $[16] = T2;
    $[17] = T3;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
    $[26] = t18;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    T0 = $[14];
    T1 = $[15];
    T2 = $[16];
    T3 = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t17 = $[25];
    t18 = $[26];
    t4 = $[27];
    t5 = $[28];
    t6 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  if (t18 !== Symbol.for("react.early_return_sentinel")) {
    return t18;
  }
  let t19;
  if ($[50] !== T0 || $[51] !== t4 || $[52] !== t5) {
    t19 = <T0 className={t4}>{t5}</T0>;
    $[50] = T0;
    $[51] = t4;
    $[52] = t5;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== T1 || $[55] !== t19 || $[56] !== t6 || $[57] !== t7) {
    t20 = <T1 className={t6}>{t7}{t19}</T1>;
    $[54] = T1;
    $[55] = t19;
    $[56] = t6;
    $[57] = t7;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== T2 || $[60] !== t20 || $[61] !== t8) {
    t21 = <T2 size={t8}>{t20}</T2>;
    $[59] = T2;
    $[60] = t20;
    $[61] = t8;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== T3 || $[64] !== t10 || $[65] !== t11 || $[66] !== t12 || $[67] !== t13 || $[68] !== t14 || $[69] !== t15 || $[70] !== t16 || $[71] !== t17 || $[72] !== t21 || $[73] !== t9) {
    t22 = <T3 as={t9} aria-label={t10} className={t11} backgroundColor={t12} backgroundImage={t13} backgroundVideo={t14} backgroundPattern={t15} patternOpacity={t16} isDark={t17}>{t21}</T3>;
    $[63] = T3;
    $[64] = t10;
    $[65] = t11;
    $[66] = t12;
    $[67] = t13;
    $[68] = t14;
    $[69] = t15;
    $[70] = t16;
    $[71] = t17;
    $[72] = t21;
    $[73] = t9;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  return t22;
};
export default PortableTwoColumn;